import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, useCallback, useRef, useState } from "react";
import {
  Upload,
  Copy,
  Download,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ScanBarcode,
  ImageIcon,
  Camera,
  BookOpen,
} from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { FaqSection } from "@/components/seo/FaqSection";
import { toast } from "sonner";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript } from "@/lib/seo";
import { decodeFromFile, formatLabel, type DecodeResult } from "@/lib/zxing";

const PATH = "/read-barcode-from-image";
const TITLE = "Read Barcode From Image — Upload a Photo to Decode";
const DESC =
  "Read a barcode from any image. Drag and drop a photo, screenshot or scanned label and ReadBarcode decodes UPC, EAN, GTIN, QR and Data Matrix in your browser.";

const FAQ = [
  {
    q: "What Image Formats Can I Upload?",
    a: "PNG, JPG/JPEG, WEBP, GIF and BMP all work. For best results use a high-resolution PNG or JPG where the barcode fills most of the frame.",
  },
  {
    q: "Is There a File Size Limit?",
    a: "There's no hard server limit — decoding happens in your browser. Very large images (over ~20 MB) may be slow on phones. A 1–3 MB photo is usually plenty.",
  },
  {
    q: "Does My Image Leave My Device?",
    a: "No. Images are decoded locally using the JavaScript port of ZXing. ReadBarcode never uploads or stores the photos you decode.",
  },
  {
    q: "Why Does the Decoder Fail on My Photo?",
    a: "Most failures come from blur, glare, low contrast, or the barcode taking up too little of the frame. Try the troubleshooting tips on this page — or crop the image to the barcode and try again.",
  },
  {
    q: "Can I read multiple barcodes from one image?",
    a: "This tool returns the first barcode it finds. For batch decoding across many images or codes, use the Batch Barcode Scanner.",
  },
  {
    q: "Can You Read a Barcode From a Picture?",
    a: "Yes. Upload the picture here and the decoder returns the encoded value and the barcode type, provided the bars or modules are sharp and the code fills a good part of the frame. The picture is decoded in your browser and never sent to a server.",
  },
  {
    q: "Do Pictures of Barcodes Work?",
    a: "A picture of a barcode holds the same data as the printed label, so this decoder or a camera-based scanner can read it. At a store checkout it depends on the scanner: camera imagers usually read a phone screen, while many laser scanners do not. A readable picture only repeats the number; it does not prove the product is genuine.",
  },
  {
    q: "Can a QR Code Be Read From an Image?",
    a: "Yes. QR Code is one of the formats this page decodes from an uploaded image, along with Data Matrix, Aztec and PDF417. For a QR code that holds a link, Wi-Fi details or a contact card, the QR Code Reader lays out the content in a more readable form.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Readers", path: "/readers" },
  { name: "Read Barcode From Image", path: PATH },
];

const TIPS = [
  {
    title: "Fill the Frame",
    body: "Crop or zoom so the barcode takes up most of the image. Tiny barcodes lost in a wide shot are the #1 reason decoding fails.",
  },
  {
    title: "Avoid Blur and Glare",
    body: "Hold the camera steady, tap to focus on the barcode, and angle slightly to dodge reflections from plastic wrap or glossy labels.",
  },
  {
    title: "Use Even Lighting",
    body: "Bright, diffuse light beats a direct flash. Harsh shadows or a flash hotspot can break the contrast the decoder needs.",
  },
  {
    title: "Keep It Straight",
    body: "1D barcodes (UPC, EAN, Code 128) decode best when the bars are roughly vertical. Heavy rotation or perspective skew hurts accuracy.",
  },
  {
    title: "Re-Shoot Wrinkled Labels",
    body: "Folded, curved or wrinkled labels deform the bars. Flatten the label or take a closer photo of just the barcode.",
  },
  {
    title: "Try a Different Format",
    body: "Screenshots from PDFs can lose detail. Export at full resolution, or open the original image instead of a chat thumbnail.",
  },
];

export const Route = createFileRoute("/read-barcode-from-image")({
  component: ImageReaderPage,
  head: () => {
    const base = pageHead({ title: TITLE, description: DESC, path: PATH });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Image Reader",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

type Status =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "success"; result: DecodeResult }
  | { kind: "error"; message: string };

function ImageReaderPage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Read a barcode from an image"
      description="Drop a photo, screenshot or scanned label and we'll decode the barcode in your browser. No upload, no signup. If the first pass finds nothing, the reader retries the image on a padded white canvas, which helps with tightly cropped barcodes that have no quiet zone."
      crumbs={CRUMBS}
      content={
        <>
          <Troubleshooting />
          <ToolSeoSections
            aboutHeading="About reading a barcode from an image"
            about={
              <>
                <p>
                  This tool reads a barcode from an image — a photo, screenshot, scanned label or any
                  image you can drop into the browser. The image is decoded locally in JavaScript, so
                  the file never leaves your device. It's a faster alternative to installing a desktop
                  app when you only need to decode one or two images.
                </p>
                <p>
                  It supports the same retail, logistics and 2D barcode formats as our camera reader, so
                  you can use it for UPC/EAN/GTIN labels, Code 128 cartons, QR codes and Data Matrix
                  tags.
                </p>
              </>
            }
            whenToUse={[
              "Decoding a barcode someone emailed or messaged you.",
              "Reading a barcode off a screenshot from a supplier portal.",
              "Extracting the GTIN from a product photo before listing it online.",
              "Recovering the value from a damaged label by photographing it under good light.",
              "Working on a laptop without a webcam or scanner gun.",
              "Batch-checking a few barcodes one image at a time.",
            ]}
            howItWorks={[
              {
                title: "Drop or choose an image",
                desc: "Drag a file into the dropzone, tap to pick from your phone, or use the file picker.",
              },
              {
                title: "Preview",
                desc: "You'll see the image plus its filename and size so you know the right file was loaded.",
              },
              {
                title: "Auto-decode",
                desc: "Decoding starts immediately — no upload step, nothing leaves the browser.",
              },
              {
                title: "Read the result",
                desc: "The right-hand panel shows the decoded value and the detected barcode format.",
              },
              {
                title: "Copy or export",
                desc: "Copy the value to the clipboard or download a small JSON record of the decode.",
              },
            ]}
            whatToAvoid={[
              "Heavily compressed JPEGs from messaging apps — edges get smeared.",
              "Photos taken at an extreme angle — straighten the barcode first.",
              "Images where the barcode is only a few pixels tall.",
              "Photographs of a screen at an angle (moiré patterns).",
              "Expecting a successful decode to confirm the product is genuine — it doesn't.",
            ]}
            tips={[
              "Crop tight to the barcode before uploading if your first attempt fails.",
              "If the photo is dark, brighten it in your phone's editor and retry.",
              "For 1D barcodes, rotate so the bars are vertical for the best chance.",
              "Screenshots usually decode better than photographs of screens.",
              "If you have multiple barcodes in one image, crop each one separately.",
            ]}
          />
          <ReadingGuide />
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <ImageReaderApp />
      <SupportedTypes />
    </ToolLayout>
  );
}

function ImageReaderApp() {
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [preview, setPreview] = useState<{ url: string; name: string; size: number } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setStatus({
        kind: "error",
        message:
          "That file doesn't look like an image. Please choose a PNG, JPG, WEBP, GIF or BMP.",
      });
      return;
    }
    // revoke previous
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return { url: URL.createObjectURL(file), name: file.name, size: file.size };
    });
    setStatus({ kind: "loading" });
    try {
      const result = await decodeFromFile(file);
      setStatus({ kind: "success", result });
    } catch {
      setStatus({
        kind: "error",
        message:
          "No barcode found in that image. Try the troubleshooting tips below or crop the image to the barcode area.",
      });
    }
  }, []);

  const reset = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setStatus({ kind: "idle" });
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
      <Card>
        <CardContent className="p-5 md:p-6">
          <label
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const file = e.dataTransfer.files?.[0];
              if (file) handleFile(file);
            }}
            className={`flex aspect-video w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
              dragOver ? "border-primary bg-primary/5" : "border-border bg-muted/40 hover:bg-muted"
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
            <Upload className="h-8 w-8 text-muted-foreground" aria-hidden />
            <div className="text-sm">
              <span className="font-medium">Drop an image of a barcode</span>
              <span className="text-muted-foreground"> or tap to choose</span>
            </div>
            <div className="text-xs text-muted-foreground">PNG · JPG · WEBP · GIF · BMP</div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={(e) => {
                e.preventDefault();
                inputRef.current?.click();
              }}
            >
              <Upload className="mr-2 h-3.5 w-3.5" />
              Choose image
            </Button>
          </label>

          {preview && (
            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="truncate">
                  <ImageIcon className="mr-1 inline h-3.5 w-3.5" />
                  {preview.name}
                </span>
                <span>{formatBytes(preview.size)}</span>
              </div>
              <div className="overflow-hidden rounded-lg border border-border bg-muted/30">
                <img
                  src={preview.url}
                  alt="Uploaded barcode preview"
                  className="mx-auto max-h-80 object-contain"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ResultPanel status={status} preview={preview} onReset={reset} />
    </div>
  );
}

function ResultPanel({
  status,
  preview,
  onReset,
}: {
  status: Status;
  preview: { name: string } | null;
  onReset: () => void;
}) {
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  const download = (result: DecodeResult) => {
    const safe = (preview?.name ?? "barcode").replace(/\.[^.]+$/, "");
    const payload = {
      source: preview?.name ?? null,
      decodedAt: new Date().toISOString(),
      format: formatLabel(result.format),
      value: result.text,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safe}-decoded.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardContent className="flex h-full flex-col p-5 md:p-6">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Result
          </p>
          <Button variant="ghost" size="sm" onClick={onReset}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" /> Clear
          </Button>
        </div>

        <div className="mt-4 flex-1">
          {status.kind === "idle" && (
            <EmptyState>
              Drop an image on the left and the decoded barcode value will appear here.
            </EmptyState>
          )}

          {status.kind === "loading" && (
            <CenterState>
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <div className="text-sm text-muted-foreground">Decoding image…</div>
            </CenterState>
          )}

          {status.kind === "error" && (
            <div className="flex flex-col items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Couldn't decode</span>
              </div>
              <p className="text-sm text-muted-foreground">{status.message}</p>
            </div>
          )}

          {status.kind === "success" && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm font-medium">Decoded</span>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Format</div>
                <Badge variant="secondary" className="mt-1">
                  {formatLabel(status.result.format)}
                </Badge>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Value</div>
                <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-md border border-border bg-muted/40 p-3 font-mono text-sm">
                  {status.result.text}
                </pre>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => copy(status.result.text)}>
                  <Copy className="mr-2 h-3.5 w-3.5" /> Copy value
                </Button>
                <Button size="sm" variant="outline" onClick={() => download(status.result)}>
                  <Download className="mr-2 h-3.5 w-3.5" /> Download JSON
                </Button>
                <Button size="sm" variant="outline" onClick={onReset}>
                  Decode another
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
      <ScanBarcode className="h-7 w-7 text-muted-foreground" aria-hidden />
      <p className="max-w-sm text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function CenterState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-2 rounded-lg border border-border bg-muted/30 p-6 text-center">
      {children}
    </div>
  );
}

function SupportedTypes() {
  return (
    <section className="mt-12 grid gap-6 md:grid-cols-2">
      <div>
        <h2 className="text-lg font-semibold">Supported Image Types</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Drop in anything your browser can render as an image — phone photos, screenshots, exported
          labels or scanned documents.
        </p>
        <ul className="mt-4 grid grid-cols-2 gap-2 text-sm">
          {["PNG", "JPG / JPEG", "WEBP", "GIF", "BMP", "HEIC (browser-dependent)"].map((t) => (
            <li key={t} className="rounded-md border border-border bg-background px-3 py-2">
              {t}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h2 className="text-lg font-semibold">Supported Barcode Formats</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          The decoder handles the major 1D and 2D symbologies — retail, logistics, books and
          healthcare.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {[
            "UPC-A",
            "UPC-E",
            "EAN-8",
            "EAN-13",
            "ITF",
            "Code 39",
            "Code 93",
            "Code 128",
            "Codabar",
            "QR Code",
            "Data Matrix",
            "Aztec",
            "PDF417",
          ].map((f) => (
            <Badge key={f} variant="outline" className="px-3 py-1 text-sm">
              {f}
            </Badge>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReadingGuide() {
  return (
    <>
      <h2>How to Read a Barcode From an Image on iPhone or Android</h2>
      <p>Open this page in your phone's browser, tap the dropzone and choose a photo, a screenshot or a new camera shot. It works as a barcode reader from image online, with no app to install.</p>
      <h3>On iPhone</h3>
      <p>Safari and Chrome offer Photo Library, Take Photo and Choose File. To get a barcode from a picture in your camera roll, pick it there; if a HEIC photo will not load, take a screenshot of it and upload that PNG.</p>
      <h3>On Android</h3>
      <p>Chrome opens the system picker with the camera, your gallery and your files. Choose the original image rather than a copy saved by a chat app, since recompression blurs thin bars.</p>
      <h2>Read a Barcode From a PDF or Screenshot</h2>
      <p>This reader takes images only, so to read a barcode from a PDF, screenshot it or export the page as PNG or JPG first.</p>
      <p>Zoom in before you capture, because the tool returns the first code it finds and small 2D codes need sharp modules. To read a Data Matrix barcode from an image of a medicine pack or a marked part, the <Link to="/data-matrix-reader">Data Matrix reader</Link> also flags a code that decodes as another format.</p>
      <h3>Decode a Barcode From a Screenshot</h3>
      <p>A screenshot copies the pixels on screen exactly, so it has no blur, glare or skew. Capture only the barcode (Windows key + Shift + S on Windows, Command + Shift + 4 on a Mac) and drop the file here; the <Link to="/barcode-guides/barcode-formats">barcode formats guide</Link> explains the symbology the result names.</p>
    </>
  );
}

function Troubleshooting() {
  return (
    <>
      <h2>Troubleshooting Blurry Barcodes</h2>
      <p>Most failed decodes come down to the photo, not the decoder. A few quick fixes:</p>
      {TIPS.map((t) => (
        <Fragment key={t.title}>
          <h3>{t.title}</h3>
          <p>{t.body}</p>
        </Fragment>
      ))}
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Scan barcodes live with your camera.",
      icon: Camera,
    },
    {
      path: "/qr-code-reader",
      label: "QR Code Reader",
      desc: "Read QR codes and parse links, Wi-Fi and contacts.",
      icon: ScanBarcode,
    },
    {
      path: "/data-matrix-reader",
      label: "Data Matrix Reader",
      desc: "Decode 2D Data Matrix labels used in healthcare and logistics.",
      icon: ScanBarcode,
    },
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Verify GTIN-8/12/13/14 check digits.",
      icon: ScanBarcode,
    },
    {
      path: "/barcode-guides/barcode-formats",
      label: "Barcode Formats Guide",
      desc: "How UPC, EAN, Code 128, QR, Data Matrix and other symbologies differ.",
      icon: BookOpen,
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Need a Different Tool?</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group flex items-start gap-3 rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <r.icon className="mt-0.5 h-4 w-4 text-primary" />
            <div>
              <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
              <div className="text-xs text-muted-foreground">{r.desc}</div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
